<template>
  <b-modal
    :id="id"
    :ref="id"
    v-model="internalVisible"
    hide-footer
    hide-header
    :hide-header-close="hideHeaderClose"
    centered
    :size="size"
    :no-close-on-backdrop="noCloseOnBackdrop"
    :no-close-on-esc="noCloseOnEsc"
    content-class="rounded-[20px] border-0 overflow-hidden shadow-2xl"
    body-class="p-0"
    @show="onShow"
    @shown="onShown"
    @hide="onHide"
    @hidden="onHidden"
  >
    <div class="p-6 md:p-7 bg-white">
      <!-- Modal Title -->
      <slot name="title">
        <h3 class="text-xl md:text-2xl font-bold text-center text-[#111827] mb-2 leading-tight">
          {{ title }}
        </h3>
      </slot>

      <!-- Modal Subtitle -->
      <slot name="subtitle">
        <p class="text-sm md:text-[14px] text-center text-[#6B7280] max-w-[340px] mx-auto leading-relaxed mb-3">
          {{ subtitle }}
        </p>
      </slot>

      <!-- OTP Method Options -->
      <slot
        name="options"
        :methods="computedMethods"
        :selected-method="selectedMethod"
        :select="handleSelect"
      >
        <div class="flex flex-col gap-3">
          <div
            v-for="(method, index) in computedMethods"
            :key="method.id || index"
            :class="[
              method.disabled
                ? 'opacity-60 cursor-not-allowed bg-gray-50 border-[#E2E2E2]'
                : 'cursor-pointer transform hover:-translate-y-0.5 active:translate-y-0',
              selectedMethod === method.id
                ? 'border-primary ring-1 ring-[#F95F17] bg-[#ffede0] shadow-sm hover:shadow-lg'
                : 'border-[#E2E2E2] bg-white hover:border-[#F95F17] hover:bg-[#ffede0] hover:shadow-lg',
            ]"
            class="flex items-center gap-3.5 p-3.5 rounded-xl border transition-all duration-200 ease-in-out select-none"
            @click="handleSelect(method)"
          >
            <!-- Option Custom Slot or Default -->
            <slot
              name="option"
              :method="method"
              :is-selected="selectedMethod === method.id"
              :select="handleSelect"
              :index="index"
            >
              <!-- Icon Container -->
              <div
                :class="[
                  method.disabled ? 'bg-[#D1D5DB] grayscale' : (method.iconBg || 'bg-[#FFF0EB]')
                ]"
                class="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              >
                <slot
                  name="icon"
                  :method="method"
                  :is-selected="selectedMethod === method.id"
                >
                  <!-- Email Icon -->
                  <img
                    v-if="method.icon === 'email'"
                    src="https://storage.googleapis.com/komerce/assets/icons/mail-at-sign.svg"
                    alt="Icon Email"
                    class="w-6 h-6"
                  >

                  <!-- WhatsApp Icon -->
                  <img
                    v-else-if="method.icon === 'whatsapp'"
                    src="https://storage.googleapis.com/komerce/assets/icons/brand-whatsapp.svg"
                    alt="Icon WhatsApp"
                    class="w-6 h-6"
                  >

                  <!-- SMS Icon -->
                  <img
                    v-else-if="method.icon === 'sms'"
                    src="https://storage.googleapis.com/komerce/assets/icons/message-orange.svg"
                    alt="Icon SMS"
                    class="w-6 h-6"
                  >

                  <!-- Custom Image URL -->
                  <b-img
                    v-else-if="method.icon"
                    :src="method.icon"
                    :alt="method.title"
                    class="w-6 h-6 object-contain"
                  />
                </slot>
              </div>

              <!-- Option Text Details -->
              <div class="flex-1 min-w-0">
                <div class="text-[15px] text-[#1F2937] leading-tight truncate">
                  {{ method.title }}
                </div>
                <div class="text-[13px] text-[#6B7280] mt-[8px] truncate">
                  {{ method.value }}
                </div>
              </div>
            </slot>
          </div>
        </div>
      </slot>

      <!-- Limit Exceeded Message -->
      <div
        v-if="isLimitExceeded"
        class="mt-2 text-center text-[14px] text-[#F04438]"
      >
        Request OTP sudah mencapai batas
      </div>

      <!-- Action Buttons Footer -->
      <slot
        name="footer"
        :selected-method="selectedMethod"
        :confirm="handleConfirm"
        :cancel="handleCancel"
        :loading="loading"
      >
        <div class="mt-6 grid grid-cols-2 gap-3">
          <!-- Cancel Button -->
          <slot name="cancel-button">
            <button
              type="button"
              class="w-full py-2.5 px-4 rounded-xl border border-[#D1D5DB] bg-white text-[#374151] font-semibold text-sm hover:bg-gray-50 focus:outline-none transition-colors duration-150"
              @click="handleCancel"
            >
              {{ cancelText }}
            </button>
          </slot>

          <!-- Confirm Button -->
          <slot name="confirm-button">
            <button
              type="button"
              :disabled="isConfirmDisabled"
              :class="[
                isConfirmDisabled
                  ? 'bg-[#C2C2C2] text-white cursor-not-allowed'
                  : 'bg-[#F95F17] text-white hover:bg-[#E05310] cursor-pointer shadow-sm',
              ]"
              class="w-full py-2.5 px-4 rounded-xl font-semibold text-sm focus:outline-none transition-colors duration-150 flex items-center justify-center gap-2"
              @click="requestOTP"
            >
              <b-spinner
                v-if="loading"
                small
                variant="light"
                class="mr-1"
              />
              <span>{{ confirmText }}</span>
            </button>
          </slot>
        </div>
      </slot>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'ModalRequestOTP',
  props: {
    id: {
      type: String,
      default: 'modal-request-otp',
    },
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Kirim Verifikasi OTP',
    },
    subtitle: {
      type: String,
      default: 'Pilih salah satu metode 2FA di bawah ini untuk mendapatkan kode verifikasi.',
    },
    email: {
      type: String,
      default: '',
    },
    phone: {
      type: String,
      default: '',
    },
    phoneNumber: {
      type: String,
      default: '',
    },
    autoMask: {
      type: Boolean,
      default: true,
    },
    methods: {
      type: Array,
      default: () => null,
    },
    availableMethods: {
      type: Array,
      default: () => ['email', 'whatsapp', 'sms'],
    },
    defaultMethod: {
      type: String,
      default: '',
    },
    selected: {
      type: String,
      default: '',
    },
    cancelText: {
      type: String,
      default: 'Batal',
    },
    confirmText: {
      type: String,
      default: 'Lanjut',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    requireSelection: {
      type: Boolean,
      default: true,
    },
    noCloseOnBackdrop: {
      type: Boolean,
      default: false,
    },
    noCloseOnEsc: {
      type: Boolean,
      default: false,
    },
    hideHeaderClose: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'md',
    },
    purposeOtp: {
      type: String,
      default: 'REVEAL_CARD',
    },
    usableToken: {
      type: String,
      default: '',
    },
    isLimitExceeded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalVisible: this.value,
      selectedMethod: this.selected || this.defaultMethod || '',
      captchaToken: '',
    }
  },
  computed: {
    effectivePhone() {
      return this.phoneNumber || this.phone
    },
    displayEmail() {
      if (!this.email) {
        return 'Email belum terdaftar di profil akun'
      }
      return this.autoMask ? this.censorEmail(this.email) : this.email
    },
    displayPhone() {
      if (!this.effectivePhone) {
        return 'Nomor telepon belum terdaftar di profil akun'
      }
      return this.autoMask ? this.censorPhone(this.effectivePhone) : this.effectivePhone
    },
    computedMethods() {
      if (this.methods && Array.isArray(this.methods) && this.methods.length > 0) {
        return this.methods.map(m => ({
          ...m,
          disabled: m.disabled || this.isLimitExceeded,
        }))
      }

      const defaultList = [
        {
          id: 'email',
          title: 'Email ke',
          value: this.displayEmail,
          icon: 'email',
          iconBg: 'bg-[#FFF0EB]',
          disabled: !this.email || this.isLimitExceeded,
        },
        {
          id: 'whatsapp',
          title: 'WhatsApp Kode OTP ke',
          value: this.displayPhone,
          icon: 'whatsapp',
          iconBg: 'bg-[#E8F8F0]',
          disabled: !this.effectivePhone || this.isLimitExceeded,
        },
        {
          id: 'sms',
          title: 'SMS Kode OTP ke',
          value: this.displayPhone,
          icon: 'sms',
          iconBg: 'bg-[#FFF0EB]',
          disabled: !this.effectivePhone || this.isLimitExceeded,
        },
      ]

      return defaultList.filter(item => this.availableMethods.includes(item.id))
    },
    isConfirmDisabled() {
      if (this.disabled || this.loading) return true
      if (this.requireSelection && !this.selectedMethod) return true
      return false
    },
  },
  watch: {
    value(newVal) {
      this.internalVisible = newVal
    },
    internalVisible(newVal) {
      this.$emit('input', newVal)
    },
    selected(newVal) {
      this.selectedMethod = newVal
    },
    defaultMethod(newVal) {
      if (!this.selectedMethod) {
        this.selectedMethod = newVal
      }
    },
  },
  mounted() {
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${process.env.VUE_APP_RECAPTCHA_TOKEN}`
    document.head.appendChild(script)
  },
  methods: {
    async requestOTP() {
      try {
        // eslint-disable-next-line no-undef
        grecaptcha.enterprise.ready(async () => {
          // eslint-disable-next-line no-undef
          const token = await grecaptcha.enterprise.execute(
            process.env.VUE_APP_RECAPTCHA_TOKEN,
            { action: this.purposeOtp },
          )
          this.captchaToken = token

          this.$http_new.post('/auth/api/v2/otp/request', {
            token: this.usableToken,
            type: this.selectedMethod,
            recaptcha_token: this.captchaToken,
          }).then(res => {
            if (res?.data?.data?.otp_limit_exceeded) {
              this.isLimitExceeded = true
              this.selectedMethod = ''
            }
            if (Number(res?.data?.data?.lock) === 1 || Number(res?.data?.lock) === 1) {
              this.hide()
              this.$emit('blocked', res?.data)
              return
            }
            this.$emit('confirm', res, this.selectedMethod)
          }).catch(err => {
            this.loading = false
            const errData = err?.response?.data
            if (Number(errData?.data?.lock) === 1 || Number(errData?.lock) === 1) {
              this.hide()
              this.$emit('blocked', errData)
              return
            }
            if (errData?.meta?.message === 'reached limit request otp') {
              this.isLimitExceeded = true
              this.selectedMethod = ''
            }
            const message = errData?.meta?.message === 'reached limit request otp' ? 'Request OTP sudah mencapai batas' : 'Gagal meminta kode OTP. Silakan coba lagi.'
            if (this.$toast_error) {
              this.$toast_error({ message })
            }
            this.$emit('error', err)
          })
        })
      } catch (error) {
        this.loading = false
        console.error(error)
      }
    },
    censorEmail(val) {
      if (!val) return ''
      if (val.includes('*')) return val
      const parts = val.split('@')
      if (parts.length !== 2) return val
      const [local, domain] = parts
      const maskedLocal = local.length > 3
        ? `${local.slice(0, 3)}${'*'.repeat(Math.max(local.length, 18))}`
        : `${local.slice(0, 1)}***`

      const domainParts = domain.split('.')
      if (domainParts.length >= 2) {
        const dName = domainParts[0]
        const ext = domainParts.slice(1).join('.')
        const maskedDomain = `${dName.charAt(0)}${'*'.repeat(Math.max(dName.length, 5))}.${ext}`
        return `${maskedLocal}@${maskedDomain}`
      }
      return `${maskedLocal}@${domain}`
    },
    censorPhone(val) {
      if (!val) return ''
      if (val.includes('*')) return val
      const cleanPhone = String(val).replace(/\s+/g, '')
      if (cleanPhone.length <= 4) return cleanPhone
      const visibleCount = 4
      const maskedCount = Math.max(cleanPhone.length - visibleCount, 8)
      return `${'*'.repeat(maskedCount)}${cleanPhone.slice(-visibleCount)}`
    },
    handleSelect(method) {
      if (method.disabled) return
      this.selectedMethod = method.id
      this.$emit('select', { id: method.id, method })
      this.$emit('change', method.id)
    },
    handleCancel() {
      this.$emit('cancel')
      this.hide()
    },
    show() {
      this.internalVisible = true
      if (this.$bvModal) {
        this.$bvModal.show(this.id)
      }
    },
    hide() {
      this.internalVisible = false
      if (this.$bvModal) {
        this.$bvModal.hide(this.id)
      }
    },
    reset() {
      this.selectedMethod = this.selected || this.defaultMethod || ''
      this.isLimitExceeded = false
    },
    onShow() {
      this.$emit('show')
    },
    onShown() {
      this.$emit('shown')
    },
    onHide() {
      this.$emit('hide')
    },
    onHidden() {
      this.$emit('hidden')
      this.reset()
    },
  },
}
</script>
